import type { CSSProperties } from 'react';
import { wrapFontNamesWithQuotes } from 'common/utils/css-font-family';
import {
	getDesignPresetInheritedAssignment,
	getDesignPresetTokenId,
	normalizePlatformStyleValue,
	resolveDesignPresetTokenParts,
	resolveDesignModeValue,
	resolveDesignTokenReference,
	type DesignDefinition,
	type DesignModeSelection,
	type DesignPreset,
	type DesignPresetProperty,
	type DesignPresetPropertyValue,
} from 'common/design-tokens';
import { getPlatformReferenceWidth, orderMediaQueryConfig } from 'common/utils/story-media-query';
import type { StoryDefaultPlatform, StoryMediaQuery } from 'types/story';
import { platformsConfig } from 'admin/utils/story-media-platforms-config';
import { DEFAULT_MEDIA_QUERY_PLATFORMS } from 'common/constants/story-media-query';
import { collapseCssSides, expandCssSides } from 'common/design-tokens/borders';
import { getPlatformDimensionPx } from 'common/design-tokens/platform-dimension';
import { NUMBER_FIELD_DECIMALS } from 'admin/components/pages/Story/CardEditor/Inspector/config/number-field';

const platformLabels: Record<string, string> = {
	fullHD: 'Full HD',
	desktop: 'Desktop',
	tablet: 'Tablet',
	mobileLandscape: 'Mobile landscape',
	mobile: 'Mobile',
};

export function getDesignPresetPlatformName(key: string, label?: string) {
	return !label || label === key ? platformLabels[key] ?? key : label;
}

/** Presentation order only; assignment inheritance keeps its own breakpoint order. */
export function orderDesignPresetPlatforms(mediaQuery: StoryMediaQuery, presets: DesignPreset[] = []) {
	const platforms = orderMediaQueryConfig(mediaQuery);
	const authoredPlatforms = new Set(
		presets.flatMap(preset =>
			Object.values(preset.properties).flatMap(assignment => Object.keys(assignment.platforms ?? {}))
		)
	);
	for (const key of authoredPlatforms) {
		if (!platforms.some(item => item.key === key))
			platforms.push({
				key: key as StoryDefaultPlatform,
				config: { uiName: key, minWidth: null, maxWidth: null },
			});
	}
	return platforms.sort(
		(a, b) => getPlatformReferenceWidth(mediaQuery, b.key) - getPlatformReferenceWidth(mediaQuery, a.key)
	);
}

// Standard Brand Kit preview only; stored Base stays portable across Stories.
export function getDesignPresetPreviewMediaQuery(defaultPlatform: StoryDefaultPlatform): StoryMediaQuery {
	return { defaultPlatform, config: platformsConfig[defaultPlatform] };
}
export const DESIGN_PRESET_PREVIEW_MEDIA_QUERY = getDesignPresetPreviewMediaQuery(
	DEFAULT_MEDIA_QUERY_PLATFORMS.DESKTOP
);

type DisplayValueInput = {
	property: DesignPresetProperty;
	assignment: DesignPresetPropertyValue;
	platform: 'base' | StoryDefaultPlatform;
	mediaQuery: StoryMediaQuery;
	design: DesignDefinition;
	modeSelection: DesignModeSelection;
};

/**
 * The value a platform shows, as the Inspector's field shows it. Inheritance resolves before converting: inherited vw
 * keeps its authored platform's reference width. A token converts like a literal, since its per-platform variable
 * renders in vw too. On its own platform a value shows as authored; converted from another, it rounds as the
 * property's Inspector field does, and every px dimension field rounds as a number field.
 */
export function getDesignPresetDisplayValue(input: DisplayValueInput) {
	return resolvePlatformValue(input, NUMBER_FIELD_DECIMALS);
}

// The precision runtime writes a converted px with
const RUNTIME_DIGITS = 2;

/** `digits` rounds a converted px value: the Inspector field's precision to show it, the runtime's to render it */
function resolvePlatformValue(
	{ property, assignment, platform, mediaQuery, design, modeSelection }: DisplayValueInput,
	digits: number
) {
	const inherited =
		platform === 'base'
			? assignment.base === undefined
				? undefined
				: { value: assignment.base, source: 'Base' }
			: getDesignPresetInheritedAssignment(assignment, platform, mediaQuery);
	if (!inherited) return undefined;
	const token = resolveDesignTokenReference(String(inherited.value), design.tokens);
	if (getDesignPresetTokenId(inherited.value) && !token) return undefined;
	// A token in part of a value, such as a border side or a shadow's color, shows as its value too
	const authoredValue = token
		? resolveDesignModeValue<string | number>(token.value, modeSelection)
		: typeof inherited.value === 'string'
			? resolveDesignPresetTokenParts(inherited.value, design, modeSelection)
			: inherited.value;
	const source = (
		inherited.source === 'Base' ? mediaQuery.defaultPlatform : inherited.source
	) as StoryDefaultPlatform;
	const cssValue =
		platform === 'base' ? authoredValue : normalizePlatformStyleValue(property, authoredValue, mediaQuery, source);
	const width = getPlatformReferenceWidth(mediaQuery, platform === 'base' ? mediaQuery.defaultPlatform : platform);
	const authoredWidth = getPlatformReferenceWidth(mediaQuery, source);
	const toPx = (part: string) => {
		const px = getPlatformDimensionPx({ value: part, authoredWidth, width, decimals: digits });
		return px === undefined ? part : `${px}px`;
	};
	// Only what runtime writes in vw converts, and a per-side border width converts side by side
	const value =
		width !== authoredWidth && typeof cssValue === 'string' && /vw/.test(cssValue)
			? collapseCssSides(expandCssSides(String(authoredValue)).map(toPx))
			: authoredValue;
	return { value, cssValue, authoredValue };
}

/** Preview uses every resolved assignment, with the same font-family serialization as runtime. */
export function getDesignPresetPreviewStyle({
	preset,
	...context
}: Omit<DisplayValueInput, 'property' | 'assignment'> & {
	preset: DesignPreset;
}): CSSProperties {
	const style = Object.fromEntries(
		Object.entries(preset.properties).flatMap(([property, assignment]) => {
			// The runtime's precision, so the preview renders what the Story does
			const display = resolvePlatformValue(
				{ ...context, property: property as DesignPresetProperty, assignment },
				RUNTIME_DIGITS
			);
			return display ? [[property, display.value]] : [];
		})
	);
	const hasFontAssignment = !!preset.properties.fontFamily || !!preset.properties.fontWeight;
	if (!hasFontAssignment) return style;
	const fontFamily =
		style.fontFamily ?? resolveDesignModeValue(context.design.defaults.root.fontFamily, context.modeSelection);
	return { ...style, fontFamily: wrapFontNamesWithQuotes(String(fontFamily)) };
}
